// Talks a driver through photographing their own vehicle's damage instead
// of handing them a bare "add photo" button -- walks the four standard
// views (front, driver's side, passenger's side, rear) one at a time,
// skipping any that aren't damaged, then asks for close-ups of the actual
// impact points. Captures happen with a live in-page camera (not a native
// camera hand-off) with a faint outline of the vehicle's body shape overlaid
// as a framing guide.
//
// Why not the plain <input type="file" capture="environment"> the rest of
// this app's photo fields use: on an iOS device where DMS is installed to
// the home screen (not just a Safari tab), that hands off to the system
// Camera app and can silently fail to hand the photo back at all -- no
// error, nothing saved, confirmed live 2026-10-01. Capturing from our own
// <video>/<canvas> instead keeps the whole thing inside the page, which
// also makes an overlay guide possible. Falls back to the native file input
// if the live preview can't start (older browser, permission denied, no
// camera) so a driver is never stuck with neither option.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { speak } from "@/lib/speech";
import { useLiveCameraCapture } from "@/lib/useLiveCameraCapture";
import { uploadIncidentPhotoBlob, removeIncidentPhoto, IncidentPhotoRef } from "@/lib/incidentPhotoUpload";
import { resolveOverlayAssetKey, overlayPublicUrl } from "@/lib/damageDiagram";
import { DamageDiagramView } from "@/lib/incidentReports";
import { Camera, Check, X as XIcon, Loader2, AlertTriangle, RotateCcw, Trash2, Sparkles } from "lucide-react";

export type GuidedPhoto = IncidentPhotoRef;

interface GuidedVehicleDamagePhotosProps {
  incidentId: string;
  vehicleType: string | null;
  existingPhotos: GuidedPhoto[];
  location: { lat: number; lng: number } | null;
  uploadedBy: string;
  onPhotosChanged: (photos: GuidedPhoto[]) => void;
}

interface ViewStep {
  view: DamageDiagramView;
  label: string;
  question: string;
}

const VIEW_STEPS: ViewStep[] = [
  { view: "front", label: "Front", question: "Is there any damage on the front of the vehicle?" },
  { view: "driver", label: "Driver's Side", question: "Is there any damage on the driver's side?" },
  { view: "passenger", label: "Passenger's Side", question: "Is there any damage on the passenger's side?" },
  { view: "rear", label: "Rear", question: "Is there any damage on the rear of the vehicle?" },
];

type Phase =
  | "intro"
  | "asking"
  | "camera"
  | "captured"
  | "closeup_intro"
  | "closeup_camera"
  | "done";

const CATEGORY = "own_vehicle_damage";

const GuidedVehicleDamagePhotos: React.FC<GuidedVehicleDamagePhotosProps> = ({
  incidentId,
  vehicleType,
  existingPhotos,
  location,
  uploadedBy,
  onPhotosChanged,
}) => {
  const [phase, setPhase] = useState<Phase>(existingPhotos.length > 0 ? "done" : "intro");
  const [stepIndex, setStepIndex] = useState(0);
  const [photos, setPhotos] = useState<GuidedPhoto[]>(existingPhotos);
  const [overlayAssetKey, setOverlayAssetKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lastCapturedView, setLastCapturedView] = useState<string | null>(null);

  const camera = useLiveCameraCapture();

  useEffect(() => {
    void resolveOverlayAssetKey(vehicleType).then(setOverlayAssetKey);
  }, [vehicleType]);

  useEffect(() => {
    return () => camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "camera" || phase === "closeup_camera") void camera.start();
    else camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const addPhoto = (photo: GuidedPhoto) => {
    setPhotos((prev) => {
      const next = [...prev, photo];
      onPhotosChanged(next);
      return next;
    });
  };

  const captureFromVideo = async (viewLabel: string) => {
    setUploading(true);
    camera.setError("");
    try {
      const blob = await camera.captureFrame();
      if (!blob) throw new Error(camera.error || "Capture failed -- try again.");
      const photo = await uploadIncidentPhotoBlob({ incidentId, category: CATEGORY, blob, filenameHint: viewLabel, location, uploadedBy });
      addPhoto(photo);
      return true;
    } catch (e) {
      camera.setError(e instanceof Error ? e.message : "Capture failed -- try again.");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const captureFromFile = async (file: File | null, viewLabel: string) => {
    if (!file) return;
    setUploading(true);
    camera.setError("");
    try {
      const photo = await uploadIncidentPhotoBlob({ incidentId, category: CATEGORY, blob: file, filenameHint: viewLabel, location, uploadedBy });
      addPhoto(photo);
      return true;
    } catch (e) {
      camera.setError(e instanceof Error ? e.message : "Upload failed -- try again.");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const currentView = VIEW_STEPS[stepIndex];

  const begin = async () => {
    setStepIndex(0);
    setPhase("asking");
    await speak(VIEW_STEPS[0].question);
  };

  const advanceView = async () => {
    const next = stepIndex + 1;
    if (next < VIEW_STEPS.length) {
      setStepIndex(next);
      setPhase("asking");
      await speak(VIEW_STEPS[next].question);
    } else {
      setPhase("closeup_intro");
      await speak(
        "Now I'd like some close-up photos of the direct damage -- things like headlights, tail lights, or the main impact area. Take as many as you need, then tap Done."
      );
      setPhase("closeup_camera");
    }
  };

  const handleYes = () => setPhase("camera");

  const handleNo = () => void advanceView();

  const handleViewCaptured = async (ok: boolean | void) => {
    if (!ok) return;
    setLastCapturedView(currentView.label);
    setPhase("captured");
    setTimeout(() => void advanceView(), 900);
  };

  const finishCloseups = async () => {
    setPhase("done");
    await speak("All done with photos.");
  };

  const removePhoto = async (photo: GuidedPhoto) => {
    try {
      await removeIncidentPhoto(photo);
      setPhotos((prev) => {
        const next = prev.filter((p) => p.id !== photo.id);
        onPhotosChanged(next);
        return next;
      });
    } catch {
      // Best-effort -- leave it in the list rather than hide a photo that's still saved.
    }
  };

  const overlayUrl = overlayAssetKey ? overlayPublicUrl(overlayAssetKey, currentView?.view) : null;

  return (
    <div className="rounded-lg border border-yellow-700/40 bg-yellow-950/10 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-yellow-500 shrink-0" />
        <p className="text-sm font-medium text-yellow-500">Vehicle Damage Photos</p>
      </div>

      {phase === "intro" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            We'll walk through the front, both sides and the rear -- skip any that aren't damaged -- then grab some close-ups.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={begin}>
            <Camera className="w-4 h-4 mr-1.5" />
            Start
          </Button>
        </div>
      )}

      {phase === "asking" && currentView && (
        <div className="space-y-3">
          <p className="text-sm text-foreground italic">"{currentView.question}"</p>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={handleYes} className="golden-button">
              <Camera className="w-4 h-4 mr-1.5" />
              Yes, take a photo
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleNo}>
              No damage here
            </Button>
          </div>
        </div>
      )}

      {(phase === "camera" || phase === "closeup_camera") && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {phase === "camera" ? `Framing: ${currentView?.label}` : "Close-up -- line up the damage and capture."}
          </p>
          <div className="relative w-full aspect-[4/3] overflow-hidden rounded-xl bg-black border border-border">
            <video
              ref={camera.videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: camera.ready ? 1 : 0 }}
            />
            {phase === "camera" && overlayUrl && camera.ready && (
              <img
                src={overlayUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-contain pointer-events-none opacity-30"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            {!camera.ready && !camera.fallback && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Starting camera...
              </div>
            )}
          </div>

          {camera.error && (
            <div className="flex items-start gap-1.5 text-xs text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{camera.error}</span>
            </div>
          )}

          {camera.fallback ? (
            <label className="inline-flex">
              <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                  Use Phone Camera
                </span>
              </Button>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={uploading}
                onChange={async (e) => {
                  const ok = await captureFromFile(e.target.files?.[0] || null, phase === "camera" ? currentView.label : "closeup");
                  e.target.value = "";
                  if (phase === "camera") void handleViewCaptured(ok);
                }}
              />
            </label>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={!camera.ready || uploading}
              onClick={async () => {
                const ok = await captureFromVideo(phase === "camera" ? currentView.label : "closeup");
                if (phase === "camera") void handleViewCaptured(ok);
              }}
              className="golden-button"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
              {uploading ? "Saving..." : "Capture"}
            </Button>
          )}

          {phase === "camera" && (
            <Button type="button" variant="ghost" size="sm" onClick={handleNo}>
              Skip this view
            </Button>
          )}

          {phase === "closeup_camera" && (
            <>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-border">
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(p)}
                        className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" variant="outline" size="sm" onClick={finishCloseups}>
                <Check className="w-4 h-4 mr-1.5" />
                Done
              </Button>
            </>
          )}
        </div>
      )}

      {phase === "captured" && (
        <div className="flex items-center gap-2 text-sm text-green-500">
          <Check className="w-4 h-4" />
          {lastCapturedView} captured.
        </div>
      )}

      {phase === "closeup_intro" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          One moment...
        </div>
      )}

      {phase === "done" && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-sm text-green-500">
            <Check className="w-4 h-4" />
            {photos.length} photo{photos.length === 1 ? "" : "s"} saved.
          </div>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-border">
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(p)}
                    className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Button type="button" variant="outline" size="sm" onClick={begin}>
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Add More / Redo Walkthrough
          </Button>
        </div>
      )}
    </div>
  );
};

export default GuidedVehicleDamagePhotos;
